import type { ChannelSlice } from '../ChannelSlice';
import { NEW_TABLE_RESET } from '../TableModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import type { FilingsWidgetModel, FilingsWidgetViewDetails } from './index';
import { Duration, Instant, LocalDate } from '@js-joda/core';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { RowIdField } from '@thinkalpha/table-client';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { t } from 'i18next';
import { optional } from 'inversify';
import type { TableStatus } from 'src/components/table-view/model';
import type { IdeaDTO } from 'src/dtos/Idea';
import type { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { ReactiveInjectable, inject, injectable } from 'src/features/ioc';
import { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import { ScreenerPlanDTO } from 'src/features/screener/dtos/ScreenerPlanDTO';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { ideaDTOToStage } from 'src/features/table/lib/ideaStages';
import { WatchlistPlanDTO } from 'src/features/watchlist/dtos/WatchlistPlanDTO';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { hideFastMovingIdeasMenuInNewsAndEvents } from 'src/lib/featureFlags/flags/hideFastMovingIdeasMenuInNewsAndEvents';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage, Stage, UnionStage } from 'src/lib/tableStages';
import type { FilingsPersistedState, FilingsWidgetAdapter } from 'src/models/FilingsWidgetAdapter';
import { filingTypesQuery, filingTagsQuery } from 'src/queries/filings';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import { filterToAllowedTypes } from 'src/routes/widgets/FilingsWidget/components/FilingTypeMenu/filingTypeAllowlist';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { SlowIdeaSource } from 'src/types/slowIdeaSource';
import { v4 } from 'uuid';

const DEFAULT_SYMBOL_RETENTION = Duration.ofSeconds(0);

type FilingsWidgetModelAction =
    | {
          type: 'INITIALIZE_SUCCESS';
          changeCloseSlug: ConcreteIndicator;
          symbolSlug: ConcreteIndicator;
          universeId: string | null;
          filingTypes: string[];
          filingTags: string[];
          symbolRetention: Duration;
      }
    | { type: 'UPDATE_FAST_IDEAS'; ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[] }
    | {
          type: 'UPDATE_SLOW_IDEAS';
          ideas: (IdeaDTO<ScreenerPlanDTO> | IdeaDTO<WatchlistPlanDTO> | { universeId: string })[];
      }
    | { type: 'UPDATE_SYMBOL_FILTER'; symbol: string | null }
    | { type: 'UPDATE_SYMBOL_RETENTION'; duration: Duration }
    | { type: 'UPDATE_SELECTED_FILING_TYPES'; types: string[] }
    | { type: 'UPDATE_SELECTED_FILING_TAGS'; tags: string[] }
    | { type: 'UPDATE_SEARCH_STRING'; searchString: string }
    | { type: 'SYMBOLS_SNAPSHOT_RECEIVED'; symbols: Iterable<string> }
    | { type: 'SYMBOLS_UPDATE_RECEIVED'; added: Iterable<string>; removed: Iterable<string> }
    | { type: 'CLEANUP_UNTRACKED_SYMBOLS' }
    | {
          type: 'SET_FILING_SUMMARY';
          filingId: string | null;
          context?: {
              date: string;
              submittedTime: string;
              uploadedTime: string;
              symbol: string;
              type: string;
              filingName: string;
              eventDetails: string | null;
              link: string;
              changeDirection: 'up' | 'down' | undefined;
          };
      }
    | { type: 'RESET' };

type FilingsWidgetModelState =
    | { type: 'initializing' }
    | {
          type: 'waiting-for-symbols';
          instanceId: string;
          changeCloseSlug: ConcreteIndicator;
          symbolSlug: ConcreteIndicator;
          universeId: string | null;
          fastIdeas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[];
          symbolFilter: string | null;
          symbolRetention: Duration;
          showFilingSummary: string | null;
          filingSummaryContext: {
              date: string;
              submittedTime: string;
              uploadedTime: string;
              symbol: string;
              type: string;
              filingName: string;
              eventDetails: string | null;
              link: string;
              changeDirection: 'up' | 'down' | undefined;
          } | null;
          filingTypes: string[];
          filingTags: string[];
          selectedFilingTypes: string[];
          selectedFilingTags: string[];
          searchString: string;
      }
    | {
          type: 'ready';
          instanceId: string;
          changeCloseSlug: ConcreteIndicator;
          symbolSlug: ConcreteIndicator;
          universeId: string | null;
          fastIdeas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[];
          symbolFilter: string | null;
          symbolRetention: Duration;
          symbols: Set<string>;
          untrackedSymbols: Map<string, Instant>;
          showFilingSummary: string | null;
          filingSummaryContext: {
              date: string;
              submittedTime: string;
              uploadedTime: string;
              symbol: string;
              type: string;
              filingName: string;
              eventDetails: string | null;
              link: string;
              changeDirection: 'up' | 'down' | undefined;
          } | null;
          filingTypes: string[];
          filingTags: string[];
          selectedFilingTypes: string[];
          selectedFilingTags: string[];
          searchString: string;
      };

export function filingsWidgetModelReducer(
    state: FilingsWidgetModelState,
    action: FilingsWidgetModelAction,
): FilingsWidgetModelState {
    switch (action.type) {
        case 'INITIALIZE_SUCCESS': {
            // Default to '8-K' if available
            const defaultSelectedTypes = action.filingTypes.includes('8-K') ? ['8-K'] : [];
            return {
                type: 'waiting-for-symbols',
                instanceId: v4(),
                changeCloseSlug: action.changeCloseSlug,
                symbolSlug: action.symbolSlug,
                universeId: action.universeId,
                fastIdeas: [],
                symbolFilter: null,
                symbolRetention: action.symbolRetention,
                showFilingSummary: null,
                filingSummaryContext: null,
                filingTypes: action.filingTypes,
                filingTags: action.filingTags,
                selectedFilingTypes: defaultSelectedTypes,
                selectedFilingTags: [],
                searchString: '',
            };
        }
        case 'UPDATE_FAST_IDEAS':
            if (state.type === 'initializing') {
                return state;
            }

            // Check if ideas have changed
            const currentIdeas = state.fastIdeas;
            const newIdeaIds = action.ideas.map(
                (idea: IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>) => idea.idea.id,
            );
            const currentIdeaIds = currentIdeas.map(
                (idea: IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>) => idea.idea.id,
            );

            const ideasChanged =
                newIdeaIds.length !== currentIdeaIds.length || !newIdeaIds.every((id) => currentIdeaIds.includes(id));

            // If ideas haven't changed, return current state
            if (!ideasChanged) {
                return state;
            }

            // If ideas changed and we were in ready state, go back to waiting for symbols
            if (state.type === 'ready') {
                return {
                    type: 'waiting-for-symbols',
                    instanceId: v4(),
                    changeCloseSlug: state.changeCloseSlug,
                    symbolSlug: state.symbolSlug,
                    universeId: state.universeId,
                    fastIdeas: action.ideas,
                    symbolFilter: state.symbolFilter,
                    symbolRetention: state.symbolRetention,
                    showFilingSummary: state.showFilingSummary,
                    filingSummaryContext: state.filingSummaryContext,
                    filingTypes: state.filingTypes,
                    filingTags: state.filingTags,
                    selectedFilingTypes: state.selectedFilingTypes,
                    selectedFilingTags: state.selectedFilingTags,
                    searchString: state.searchString,
                };
            }

            // Otherwise just update fastIdeas
            return {
                ...state,
                fastIdeas: action.ideas,
            };
        case 'UPDATE_SLOW_IDEAS':
            if (state.type === 'initializing') {
                return state;
            }

            // Check for universe ID changes
            const slowIdeasWithUniverse = action.ideas.filter((idea) => 'universeId' in idea);
            const newUniverse = slowIdeasWithUniverse.length > 0 ? slowIdeasWithUniverse[0].universeId : null;

            // If universe hasn't changed, return current state
            if (state.universeId === newUniverse) {
                return state;
            }

            // If we're in ready state and universe changed, go back to waiting for symbols
            if (state.type === 'ready') {
                return {
                    type: 'waiting-for-symbols',
                    instanceId: v4(),
                    changeCloseSlug: state.changeCloseSlug,
                    symbolSlug: state.symbolSlug,
                    universeId: newUniverse,
                    fastIdeas: state.fastIdeas,
                    symbolFilter: state.symbolFilter,
                    symbolRetention: state.symbolRetention,
                    showFilingSummary: state.showFilingSummary,
                    filingSummaryContext: state.filingSummaryContext,
                    filingTypes: state.filingTypes,
                    filingTags: state.filingTags,
                    selectedFilingTypes: state.selectedFilingTypes,
                    selectedFilingTags: state.selectedFilingTags,
                    searchString: state.searchString,
                };
            }

            // Update universeId for waiting-for-symbols state
            return {
                ...state,
                universeId: newUniverse,
            };
        case 'UPDATE_SYMBOL_FILTER':
            if (state.type === 'initializing') {
                return state;
            }
            return {
                ...state,
                symbolFilter: action.symbol,
                ...(state.type === 'ready' ? { untrackedSymbols: new Map() } : {}),
            };
        case 'UPDATE_SYMBOL_RETENTION':
            if (state.type === 'initializing') {
                return state;
            }

            if (state.type !== 'ready') {
                return {
                    ...state,
                    symbolRetention: action.duration,
                };
            }

            let untrackedSymbols = state.untrackedSymbols;
            if (action.duration.isZero()) {
                untrackedSymbols = new Map();
            }

            return {
                ...state,
                symbolRetention: action.duration,
                untrackedSymbols,
            };
        case 'UPDATE_SELECTED_FILING_TYPES':
            if (state.type === 'initializing') {
                return state;
            }
            // If 8-K is deselected, clear all tags
            return {
                ...state,
                selectedFilingTypes: action.types,
                selectedFilingTags: action.types.includes('8-K') ? state.selectedFilingTags : [],
            };
        case 'UPDATE_SELECTED_FILING_TAGS':
            if (state.type === 'initializing') {
                return state;
            }
            return {
                ...state,
                selectedFilingTags: action.tags,
            };
        case 'UPDATE_SEARCH_STRING':
            if (state.type === 'initializing') {
                return state;
            }
            return {
                ...state,
                searchString: action.searchString,
            };
        case 'SYMBOLS_SNAPSHOT_RECEIVED':
            if (state.type === 'initializing') {
                return state;
            } else if (state.type === 'ready') {
                const newSymbolsSet = new Set(action.symbols);

                // when we have a symbolFilter we won;'t have untracked symbols
                if (state.symbolFilter) {
                    return {
                        ...state,
                        symbols: newSymbolsSet,
                        untrackedSymbols: new Map(),
                    };
                }

                const currentSymbolsSet = state.symbols;
                const untrackedMap = new Map(state.untrackedSymbols);
                const now = Instant.now();

                // Find symbols that are being removed (in current but not in new)
                // Add them to untracked but keep them in symbols until cleanup
                for (const symbol of currentSymbolsSet) {
                    if (!state.symbolRetention.isZero() && !newSymbolsSet.has(symbol) && !state.symbolFilter) {
                        untrackedMap.set(symbol, now);
                    }
                }

                // Add new symbols and remove them from untracked if they were there
                for (const symbol of newSymbolsSet) {
                    untrackedMap.delete(symbol);
                }

                // Combine new symbols with existing untracked symbols that haven't expired
                const combinedSymbols = new Set([...newSymbolsSet, ...untrackedMap.keys()]);

                return {
                    ...state,
                    symbols: combinedSymbols,
                    untrackedSymbols: untrackedMap,
                };
            } else {
                return {
                    ...state,
                    type: 'ready',
                    symbols: new Set(action.symbols),
                    untrackedSymbols: new Map(),
                };
            }
        case 'SYMBOLS_UPDATE_RECEIVED':
            if (state.type === 'initializing') {
                return state;
            } else if (state.type === 'ready') {
                const currentSymbols = new Set(state.symbols);
                let untrackedMap = new Map(state.untrackedSymbols);
                const now = Instant.now();

                // Add new symbols and remove them from untracked if present
                for (const symbol of action.added) {
                    currentSymbols.add(symbol);
                    untrackedMap.delete(symbol);
                }

                if (!state.symbolRetention.isZero() && !state.symbolFilter) {
                    // Add removed symbols to untracked but keep them in symbols until cleanup
                    // if retention is not zero
                    for (const symbol of action.removed) {
                        untrackedMap.set(symbol, now);
                    }
                } else {
                    // if retention is zero, remove all symbols from untracked
                    for (const symbol of action.removed) {
                        currentSymbols.delete(symbol);
                    }
                    // reset untracked map to empty when retention is zero or when we have a symbolFilter
                    untrackedMap = new Map();
                }

                return {
                    ...state,
                    symbols: currentSymbols,
                    untrackedSymbols: untrackedMap,
                };
            } else {
                return {
                    ...state,
                    type: 'ready',
                    symbols: new Set(action.added),
                    untrackedSymbols: new Map(),
                };
            }
        case 'CLEANUP_UNTRACKED_SYMBOLS': {
            if (state.type !== 'ready') {
                return state;
            }

            const cutoffTime = Instant.now().minus(state.symbolRetention);
            const untrackedMap = new Map(state.untrackedSymbols);
            const currentSymbols = new Set(state.symbols);
            let hasChanges = false;

            for (const [symbol, removalTime] of untrackedMap) {
                if (removalTime.isBefore(cutoffTime)) {
                    untrackedMap.delete(symbol);
                    currentSymbols.delete(symbol);
                    hasChanges = true;
                }
            }

            if (hasChanges) {
                return {
                    ...state,
                    symbols: currentSymbols,
                    untrackedSymbols: untrackedMap,
                };
            }
            return state;
        }
        case 'SET_FILING_SUMMARY':
            if (state.type === 'initializing') {
                return state;
            }
            return {
                ...state,
                showFilingSummary: action.filingId,
                filingSummaryContext: action.context ?? null,
            };
        case 'RESET':
            return { type: 'initializing' };
        default:
            return state;
    }
}

@injectable()
export class FilingsWidgetModelImpl extends ReactiveInjectable implements FilingsWidgetModel {
    #logger: Logger;
    #store: ReactBindings['Store'];
    #queryClient: ReactBindings['QueryClient'];
    #formulaService: ReactBindings['FormulaService'];
    #table: ReactBindings['TableModel'];
    #adapter: FilingsWidgetAdapter;
    #defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'];
    #slugMapService: ReactBindings['SlugMapService'];
    #slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);
    #rowIdToSymbolMap = new Map<number, string>();
    #symbolChangeDirectionsMap = signal<Map<string, number>>(new Map());
    #stateSignal = signal<FilingsWidgetModelState>({ type: 'initializing' });
    #devtools: ReducerDevtoolsConnection<FilingsWidgetModelState, FilingsWidgetModelAction> | undefined;
    #slowIdeaSourceSignal = signal<SlowIdeaSource | null>(null);
    // Convenience computeds for internal consumption
    #universeId = computed(() => {
        const s = this.#slowIdeaSourceSignal.value;
        return s?.type === 'universe' ? s.id : null;
    });
    #slowIdeaDTO = computed(() => {
        const s = this.#slowIdeaSourceSignal.value;
        return s?.type === 'idea' ? s.dto : null;
    });
    #gotoDateSignal = signal<LocalDate | null>(null);
    #viewDetailsSignal = signal<FilingsWidgetViewDetails>({ type: 'loading' });

    // Private computed signals derived from #stateSignal
    #symbols = computed<ReadonlySet<string> | null>(() => {
        const state = this.#stateSignal.value;
        return state.type === 'ready' ? state.symbols : null;
    });

    #symbolFilter = computed<string | null>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.symbolFilter : null;
    });

    #symbolRetention = computed<Duration | null>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.symbolRetention : null;
    });

    #fastIdeas = computed<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[] | null>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.fastIdeas : null;
    });

    #showFilingSummary = computed<string | null>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.showFilingSummary : null;
    });

    #filingSummaryContext = computed<{
        date: string;
        submittedTime: string;
        uploadedTime: string;
        symbol: string;
        type: string;
        filingName: string;
        eventDetails: string | null;
        link: string;
        changeDirection: 'up' | 'down' | undefined;
    } | null>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.filingSummaryContext : null;
    });

    #filingTypes = computed<string[]>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.filingTypes : [];
    });

    #filingTags = computed<string[]>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.filingTags : [];
    });

    #selectedFilingTypes = computed<string[]>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.selectedFilingTypes : [];
    });

    #selectedFilingTags = computed<string[]>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.selectedFilingTags : [];
    });

    #searchString = computed<string>(() => {
        const state = this.#stateSignal.value;
        return state.type !== 'initializing' ? state.searchString : '';
    });

    #channelSlice!: ChannelSlice;
    #widgetTabSlice!: WidgetTabSlice;

    #connectionStatus = signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    >({
        status: 'connecting',
        progress: undefined,
        failureCount: 0,
    });

    filingsControlsHeaderElements: WidgetHeaderElement[] = [
        { type: 'overflowDropdown', position: 0, alwaysVisible: true },
        { type: 'spacer', position: 5, alwaysVisible: true },
        { type: 'filingsGoToDate', position: 10, priority: 1 },
        { type: 'filingsFilingTypeFilter', position: 60, priority: 2 },
        { type: 'filingsFilingTagsFilter', position: 70, priority: 3 },
    ];

    headerElements!: WidgetHeaderElement[];

    eventBus!: FilingsWidgetModel['eventBus'];

    constructor(
        @inject('FilingsWidgetAdapter') adapter: FilingsWidgetAdapter,
        @inject('Store') store: ReactBindings['Store'],
        @inject('Logger') logger: Logger,
        @inject('DefaultUniverseServiceProvider')
        defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('TableModel') table: ReactBindings['TableModel'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') formulaService: ReactBindings['FormulaService'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);
        this.#adapter = adapter;
        this.#store = store;
        this.#queryClient = queryClient;
        this.#formulaService = formulaService;
        this.#table = table;
        this.#defaultUniverseServiceProvider = defaultUniverseServiceProvider;
        this.#slugMapService = slugMapService;
        this.#slugMapServiceProvider = slugMapServiceProvider;
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#logger = logger.getSubLogger({ name: 'FilingsWidgetModel' });

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        // Build the EventBus for the new header system
        this.eventBus = this.#buildEventBus();
    }

    async init(tabId?: string) {
        const baseElements = tabId
            ? COMMON_HEADER_ELEMENTS
            : STANDALONE_COMMON_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = [
            ...baseElements,
            { type: 'symbolPicker', position: 25, priority: 80, placeholder: 'symbol' },
            { type: 'symbolRetention', position: 28, priority: 50 },
            {
                type: 'ideasMenu',
                key: 'slow',
                position: 54,
                priority: 60,

                selectType: 'single',
                ideaType: 'slow',
                ideaPlanTypes: ['universe', 'screener', 'watchlist'],
                showSelected: false,
                allowDelete: false,
            },
            {
                type: 'ideasMenu',
                key: 'fast',
                position: 56,
                priority: 60,

                selectType: 'multi',
                ideaType: 'fast',
                ideaPlanTypes: ['scanner', 'if-then'],
                showSelected: true,
                allowDelete: false,
            },
            { type: 'connectionStatus', position: 995, priority: 995 },
        ];

        this.#devtools = connectReducerDevtools({ name: `Filings [${tabId ?? 'standalone'}]` });
        if (this.#devtools) {
            this.disposableStack.use(this.#devtools);
            this.#devtools.init(this.#stateSignal.value);
        }

        if (tabId) {
            // Initialize slices for the new widget header system (workspace only)
            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);
        }

        // Check feature flags and update header elements
        const hideFastMovingIdeasMenu = hideFastMovingIdeasMenuInNewsAndEvents.select(this.#store.getState());

        // Filter out fast ideas menu if feature flag is enabled
        this.headerElements = this.headerElements.filter((element) => {
            // Remove fast ideas menu if hideFastMovingIdeasMenu is true
            if (element.type === 'ideasMenu' && element.ideaType === 'fast') {
                return !hideFastMovingIdeasMenu;
            }
            return true;
        });

        this.#gotoDateSignal.value = this.#adapter.getState().gotoDate;

        // The adapter's universeId field stores either a universe ID or a screener/watchlist idea ID.
        // Probe the stored value to determine which on init.
        const storedId = this.#adapter.getState().universeId;
        if (storedId) {
            try {
                const dto = await this.#queryClient.fetchQuery(
                    getIdeaDTOByIdQuery(this.#queryClient, this.#formulaService, this.#slugMapService, storedId),
                );
                if (TRACE) {
                    this.#logger.trace('Initializing with slow idea from adapter', { ideaId: storedId });
                }
                this.#slowIdeaSourceSignal.value = {
                    type: 'idea',
                    id: storedId,
                    dto: dto as IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>,
                };
            } catch {
                if (TRACE) {
                    this.#logger.trace('Initializing with universe from adapter', { universeId: storedId });
                }
                this.#slowIdeaSourceSignal.value = { type: 'universe', id: storedId };
            }
        } else {
            const defaultUniverseService = await this.#defaultUniverseServiceProvider();
            const defaultUniverseId = defaultUniverseService.universe.value.id;
            if (TRACE) {
                this.#logger.trace('Initializing with default universe', { defaultUniverseId });
            }
            this.#adapter.persist({ universeId: defaultUniverseId });
            this.#slowIdeaSourceSignal.value = { type: 'universe', id: defaultUniverseId };
        }

        this.disposableStack.defer(
            this.#adapter.subscribe((adapterState: FilingsPersistedState) => {
                const newStoredId = adapterState.universeId;
                const currentId = this.#slowIdeaSourceSignal.value?.id ?? null;

                if (newStoredId !== currentId) {
                    if (
                        newStoredId !== null &&
                        this.#slowIdeaSourceSignal.value?.type === 'idea' &&
                        newStoredId === currentId
                    ) {
                        // Adapter echoing back the idea ID we already set — no-op
                    } else {
                        if (TRACE) {
                            this.#logger.trace({
                                message: 'Adapter changed slow idea source',
                                universeId: newStoredId,
                            });
                        }
                        // Treat adapter-driven changes as universe selections (ideas are set in-memory first)
                        this.#slowIdeaSourceSignal.value = newStoredId ? { type: 'universe', id: newStoredId } : null;
                        if (this.#stateSignal.value.type !== 'initializing') {
                            this.#updateState({
                                type: 'UPDATE_SLOW_IDEAS',
                                ideas: newStoredId ? [{ universeId: newStoredId }] : [],
                            });
                        }
                    }
                }

                // Update symbolFilter in state
                if (this.#stateSignal.value.type !== 'initializing') {
                    // Only update if the values are actually different, treating null and empty string as equivalent
                    const currentValue = this.#symbolFilter.value;
                    const newValue = adapterState.symbolFilter ?? null;

                    // Skip update if they're identical or if one is null and the other is empty string
                    if (
                        currentValue !== newValue &&
                        !((currentValue === null && newValue === '') || (currentValue === '' && newValue === null))
                    ) {
                        this.#updateState({ type: 'UPDATE_SYMBOL_FILTER', symbol: adapterState.symbolFilter });
                    }
                }

                // Sync gotoDate signal
                const adapterGotoDate = adapterState.gotoDate;
                if (adapterGotoDate !== this.#gotoDateSignal.value) {
                    if (TRACE) {
                        this.#logger.trace('gotoDate changed', {
                            oldValue: this.#gotoDateSignal.value,
                            newValue: adapterGotoDate,
                        });
                    }
                    this.#gotoDateSignal.value = adapterGotoDate;
                }

                // Update symbolRetention in state
                if (this.#stateSignal.value.type !== 'initializing') {
                    const adapterRetention = adapterState.symbolRetention;
                    const reduxDuration = adapterRetention
                        ? Duration.parse(adapterRetention)
                        : DEFAULT_SYMBOL_RETENTION;
                    const currentDuration = this.#symbolRetention.value;
                    if (currentDuration && !reduxDuration.equals(currentDuration)) {
                        this.#updateState({ type: 'UPDATE_SYMBOL_RETENTION', duration: reduxDuration });
                    }
                }

                // Update selectedFilingTypes in state
                if (this.#stateSignal.value.type !== 'initializing') {
                    const adapterFilingTypes = adapterState.filingTypes;
                    const currentFilingTypes = this.#selectedFilingTypes.value;

                    // Compare arrays to see if they're different
                    const typesChanged =
                        adapterFilingTypes.length !== currentFilingTypes.length ||
                        !adapterFilingTypes.every((type) => currentFilingTypes.includes(type));

                    if (typesChanged) {
                        this.#updateState({ type: 'UPDATE_SELECTED_FILING_TYPES', types: adapterFilingTypes });
                    }
                }
            }),
        );

        const slugMapService = await this.#slugMapServiceProvider();

        const symbolSlug = computed(() => slugMapService.slugMap.value[Slugs.symbol]);
        const changeCloseSlug = computed(() => slugMapService.slugMap.value[Slugs.symbolChangeClose]);

        // Load filing types and tags
        const [filingTypesResult, filingTagsResult] = await Promise.all([
            this.#queryClient.fetchQuery(filingTypesQuery()),
            this.#queryClient.fetchQuery(filingTagsQuery()),
        ]);

        // Hydrate symbolRetention from adapter, or use default if not set
        const adapterSymbolRetention = this.#adapter.getState().symbolRetention;
        const initialSymbolRetention = adapterSymbolRetention
            ? Duration.parse(adapterSymbolRetention)
            : DEFAULT_SYMBOL_RETENTION;

        this.#updateState({
            type: 'INITIALIZE_SUCCESS',
            changeCloseSlug: changeCloseSlug.value,
            symbolSlug: symbolSlug.value,
            universeId: this.#universeId.value,
            filingTypes: filterToAllowedTypes(filingTypesResult),
            filingTags: filingTagsResult,
            symbolRetention: initialSymbolRetention,
        });

        // Sync initial state from adapter (for standalone mode where URL params may differ from defaults)
        const adapterFilingTypes = filterToAllowedTypes(this.#adapter.getState().filingTypes);
        if (adapterFilingTypes.length > 0) {
            this.#updateState({ type: 'UPDATE_SELECTED_FILING_TYPES', types: adapterFilingTypes });
        } else if (!tabId) {
            // Adapter has no filing types (URL didn't specify any) — sync the model's default
            // back to the adapter so the subscription doesn't clear them on the next persist call
            const state = this.#stateSignal.value;
            if (state.type !== 'initializing' && state.selectedFilingTypes.length > 0) {
                this.#adapter.persist({ filingTypes: state.selectedFilingTypes });
            }
        }
        const adapterSymbolFilter = this.#adapter.getState().symbolFilter;
        if (adapterSymbolFilter !== null) {
            this.#updateState({ type: 'UPDATE_SYMBOL_FILTER', symbol: adapterSymbolFilter });
        }

        // Initialize query options signal
        const queryOptions: Signal<QueryObserverOptions<FetchableTableCreationResultWithColumns>> = computed(() => {
            return {
                queryKey: [
                    'filings-widget',
                    'table',
                    this.symbolFilter?.value ?? null,
                    this.#slowIdeaSourceSignal.value?.id ?? null,
                    (this.#fastIdeas.value ?? []).map((idea) => idea.idea.id),
                ],
                queryFn: async () => {
                    const symbolFilterValue = this.symbolFilter?.value ?? null;
                    const source = this.#slowIdeaSourceSignal.value;
                    let stage: Stage;
                    if (symbolFilterValue) {
                        stage = SourceStage.allForKey('ticker');
                    } else if (source?.type === 'idea') {
                        const allStage = SourceStage.allForKey('ticker');
                        stage = ideaDTOToStage(allStage, source.dto);
                    } else if (source?.type === 'universe') {
                        stage = SourceStage.fromTickerUniverseId(source.id);
                    } else {
                        stage = SourceStage.allForKey('ticker');
                    }

                    if (symbolFilterValue) {
                        stage = FilterStage.fromInputStage(stage, {
                            type: 'filter',
                            formula: `symbol == "${symbolFilterValue}"`,
                            imports: [{ ...symbolSlug.value, alias: 'symbol' }],
                        });
                    } else {
                        // Handle fast ideas
                        const fastIdeasValue = this.#fastIdeas.value ?? [];
                        if (fastIdeasValue.length > 1) {
                            const stages = fastIdeasValue.map((idea) => ideaDTOToStage(stage, idea));

                            if (stages.length > 0) {
                                stage = UnionStage.from(stages);
                            }
                        } else if (fastIdeasValue.length === 1) {
                            const idea = fastIdeasValue[0];
                            stage = ideaDTOToStage(stage, idea);
                        }
                    }

                    const formula = 'changeClose > 0 ? 1 : changeClose < 0 ? -1 : 0';
                    const imports = [{ ...changeCloseSlug.value, alias: 'changeClose' }];

                    stage = OutputStage.fromInputStage(stage, {}, [
                        { id: 'symbol', type: 'indicator', indicator: symbolSlug.value },
                        { id: 'changeDirection', type: 'formula', formula, imports },
                    ]);

                    return {
                        ...(await createTable({
                            leafStage: stage.build(),
                        })),
                        fetchMode: 'snapshot',
                    };
                },
                enabled: true,
                staleTime: 0,
                gcTime: 0,
                refetchOnWindowFocus: false,
            };
        });

        this.#table.init({
            initBounds: { firstRow: 0, windowSize: 100000 },
            initThrottle: 1000,
            queryOptions,
            devtoolsName: 'Filings/table',
        });

        // Subscribe to table updates
        this.disposableStack.use(
            this.#table.updatesWithReset$.subscribe((updates) => {
                if (!updates) return;

                if (updates === NEW_TABLE_RESET) {
                    this.#updateState({
                        type: 'SYMBOLS_SNAPSHOT_RECEIVED',
                        symbols: [],
                    });
                    this.#symbolChangeDirectionsMap.value = new Map();
                    return;
                }

                if (updates.type === 'snapshot') {
                    this.#rowIdToSymbolMap.clear();
                    const newSymbols = new Set<string>();
                    const changeDirections = new Map<string, number>();

                    for (const update of updates) {
                        this.#rowIdToSymbolMap.set(update[RowIdField]!, update.symbol);
                        newSymbols.add(update.symbol);

                        const changeDirection = Number(update['changeDirection']);
                        if (!isNaN(changeDirection)) {
                            changeDirections.set(update.symbol, changeDirection);
                        }
                    }

                    this.#symbolChangeDirectionsMap.value = changeDirections;

                    this.#updateState({
                        type: 'SYMBOLS_SNAPSHOT_RECEIVED',
                        symbols: newSymbols,
                    });
                    return;
                } else if (updates.type === 'diff') {
                    const removedSymbols = new Set<string>();
                    const addedSymbols = new Set<string>();
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.value);

                    for (const removed of updates.deletedRowIds) {
                        const deadSymbol = this.#rowIdToSymbolMap.get(removed);
                        if (!deadSymbol) continue;
                        this.#rowIdToSymbolMap.delete(removed);
                        changeDirections.delete(deadSymbol);
                        removedSymbols.add(deadSymbol);
                    }

                    for (const update of updates) {
                        if (TRACE && removedSymbols.has(update.symbol)) {
                            this.#logger.trace('Found update for removed symbol', {
                                symbol: update.symbol,
                                rowId: update[RowIdField],
                            });
                        }
                        const symbol = update['symbol'] as string;
                        if (symbol) {
                            // Only consider it an addition if it's not a symbol we already know about
                            const currentlyTracked = this.#symbols.value !== null && this.#symbols.value.has(symbol);

                            const changeDirection = Number(update['changeDirection']);
                            if (!isNaN(changeDirection)) {
                                changeDirections.set(symbol, changeDirection);
                            }

                            if (!currentlyTracked) {
                                this.#rowIdToSymbolMap.set(update[RowIdField]!, update.symbol);
                                addedSymbols.add(symbol);
                            }
                        }
                    }

                    this.#symbolChangeDirectionsMap.value = changeDirections;

                    if (addedSymbols.size > 0 || removedSymbols.size > 0) {
                        if (TRACE) {
                            for (const symbol of addedSymbols) {
                                this.#logger.trace(`New symbol added to table: ${symbol}`);
                            }
                            for (const symbol of removedSymbols) {
                                this.#logger.trace(`Symbol removed from table: ${symbol}`);
                            }
                        }
                        this.#updateState({
                            type: 'SYMBOLS_UPDATE_RECEIVED',
                            added: addedSymbols,
                            removed: removedSymbols,
                        });
                    }
                } else if (updates.type === 'quote') {
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.value);

                    for (const update of updates) {
                        const symbol = update.symbol;
                        if (symbol) {
                            const changeDirection = Number(update['changeDirection']);
                            if (!isNaN(changeDirection)) {
                                changeDirections.set(symbol, changeDirection);
                            }
                        }
                    }

                    this.#symbolChangeDirectionsMap.value = changeDirections;
                }
            }),
        );

        this.disposableStack.use(
            this.#table.count$.subscribe((count) => {
                if (count === 0 && this.#stateSignal.value.type === 'waiting-for-symbols') {
                    // If we have no symbols and were waiting for them, reset the state
                    if (TRACE) {
                        this.#logger.trace('No symbols found, resetting state');
                    }
                    this.#updateState({ type: 'SYMBOLS_SNAPSHOT_RECEIVED', symbols: [] });
                }
            }),
        );

        // Set up periodic cleanup of untracked symbols
        const cleanupInterval = setInterval(() => {
            this.#updateState({ type: 'CLEANUP_UNTRACKED_SYMBOLS' });
        }, 5000); // 5 seconds
        this.disposableStack.defer(() => clearInterval(cleanupInterval));

        // Sync connection status for new header system
        this.disposableStack.defer(
            effect(() => {
                const status = this.#table.connectionStatus.value;
                const progress = this.#table.progress.value;
                this.#connectionStatus.value = {
                    status,
                    progress,
                    failureCount: 0,
                };
            }),
        );
    }

    get viewDetails(): ReadonlySignal<FilingsWidgetViewDetails> {
        return this.#viewDetailsSignal;
    }

    get symbols(): ReadonlySignal<ReadonlySet<string> | null> {
        return this.#symbols;
    }

    get symbolFilter(): ReadonlySignal<string | null> {
        return this.#symbolFilter;
    }

    get symbolRetention(): ReadonlySignal<Duration> | null {
        if (this.#symbolRetention.value === null) {
            return null;
        }
        return computed(() => this.#symbolRetention.value!);
    }

    get symbolChangeDirections(): ReadonlySignal<ReadonlyMap<string, number>> {
        return this.#symbolChangeDirectionsMap;
    }

    onChangeSymbolFilter(value: string) {
        // Don't dispatch if the value is already the same to prevent loops
        // This is especially important for empty string vs null comparisons
        const currentValue = this.#symbolFilter.value;
        if ((value === '' && currentValue === null) || value === currentValue) {
            return;
        }
        this.#adapter.persist({ symbolFilter: value === '' ? null : value });
    }

    onChangeSymbolRetention(duration: Duration) {
        this.#adapter.persist({ symbolRetention: duration.toString() });
    }

    get slowIdeaSource(): ReadonlySignal<SlowIdeaSource | null> {
        return this.#slowIdeaSourceSignal;
    }

    get fastIdeas(): ReadonlySignal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]> | null {
        if (this.#fastIdeas.value === null) {
            return null;
        }
        return computed(() => this.#fastIdeas.value!);
    }

    private isUniverseIdFormat(idea: any): idea is { universeId: string } {
        return idea && typeof idea === 'object' && 'universeId' in idea && typeof idea.universeId === 'string';
    }

    private isTypeIdFormat(idea: any): idea is { type: string; id: string } {
        return (
            idea &&
            typeof idea === 'object' &&
            'type' in idea &&
            idea.type === 'universe' &&
            'id' in idea &&
            typeof idea.id === 'string'
        );
    }

    onSelectSlowIdeas(
        ideas: (
            | IdeaDTO<ScreenerPlanDTO>
            | IdeaDTO<WatchlistPlanDTO>
            | { universeId: string }
            | { type: string; id: string }
        )[],
    ): void {
        if (TRACE) {
            this.#logger.trace('onSelectSlowIdeas received ideas', {
                ideas: JSON.stringify(ideas),
                currentSource: this.#slowIdeaSourceSignal.value,
            });
        }

        const firstIdea = ideas[0];

        if (!firstIdea) {
            this.#slowIdeaSourceSignal.value = null;
            this.#adapter.persist({ universeId: null });
            return;
        }

        // Check for IdeaDTO (screener or watchlist) — has an 'idea' property
        if ('idea' in firstIdea) {
            const ideaDTO = firstIdea as IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>;
            const ideaId = ideaDTO.idea.id ?? null;
            if (TRACE) {
                this.#logger.trace('Selected screener/watchlist slow idea', { ideaId });
            }
            // Set in-memory signal first so the adapter subscription sees the current ID
            this.#slowIdeaSourceSignal.value = ideaId ? { type: 'idea', id: ideaId, dto: ideaDTO } : null;
            // Persist the idea ID in the adapter's universeId field
            if (ideaId !== this.#adapter.getState().universeId) {
                this.#adapter.persist({ universeId: ideaId });
            }
            return;
        }

        // Universe selection
        let selectedUniverse: string | null = null;

        const ideaWithUniverseId = ideas.find((idea) => this.isUniverseIdFormat(idea));
        if (ideaWithUniverseId) {
            selectedUniverse = ideaWithUniverseId.universeId;
        } else {
            const ideaWithTypeUniverse = ideas.find((idea) => this.isTypeIdFormat(idea));
            if (ideaWithTypeUniverse) {
                selectedUniverse = ideaWithTypeUniverse.id;
            }
        }

        if (TRACE) {
            this.#logger.trace('Selected universe', {
                selectedUniverse,
                currentSource: this.#slowIdeaSourceSignal.value,
            });
        }

        const currentId = this.#slowIdeaSourceSignal.value?.id ?? null;
        if (currentId !== selectedUniverse) {
            this.#slowIdeaSourceSignal.value = selectedUniverse ? { type: 'universe', id: selectedUniverse } : null;
            this.#adapter.persist({ universeId: selectedUniverse });
        }
    }

    onSelectFastIdeas(ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]): void {
        this.#updateState({ type: 'UPDATE_FAST_IDEAS', ideas });
        const ideaIds = ideas.map((idea) => idea.idea.id!);
        this.#adapter.persist({ fastIdeasIds: ideaIds });
    }

    onSelectSymbol(symbol: string) {
        this.#adapter.onSelectSymbol(symbol);
    }

    openChartForSymbol(symbol: string) {
        this.#adapter.openChartForSymbol(symbol);
    }

    openLensForSymbol(symbol: string) {
        this.#adapter.openLensForSymbol(symbol);
    }

    get showFilingSummary(): ReadonlySignal<string | null> {
        return this.#showFilingSummary;
    }

    get filingSummaryContext(): ReadonlySignal<{
        date: string;
        submittedTime: string;
        uploadedTime: string;
        symbol: string;
        type: string;
        filingName: string;
        eventDetails: string | null;
        link: string;
        changeDirection: 'up' | 'down' | undefined;
    } | null> {
        return this.#filingSummaryContext;
    }

    onRowClick(
        filingId: string | null,
        context?: {
            date: string;
            submittedTime: string;
            uploadedTime: string;
            symbol: string;
            type: string;
            filingName: string;
            eventDetails: string | null;
            link: string;
            changeDirection: 'up' | 'down' | undefined;
        },
    ) {
        // Toggle behavior: if clicking the same filing, close it
        const currentFilingId = this.#showFilingSummary.value;
        const newFilingId = currentFilingId === filingId ? null : filingId;
        this.#updateState({
            type: 'SET_FILING_SUMMARY',
            filingId: newFilingId,
            context: newFilingId ? context : undefined,
        });
    }

    get filingTypes(): ReadonlySignal<string[]> {
        return this.#filingTypes;
    }

    get filingTags(): ReadonlySignal<string[]> {
        return this.#filingTags;
    }

    get selectedFilingTypes(): ReadonlySignal<string[]> {
        return this.#selectedFilingTypes;
    }

    get selectedFilingTags(): ReadonlySignal<string[]> {
        return this.#selectedFilingTags;
    }

    get searchString(): ReadonlySignal<string> {
        return this.#searchString;
    }

    get gotoDateSignal(): ReadonlySignal<LocalDate | null> {
        return this.#gotoDateSignal;
    }

    onSelectFilingTypes(types: string[]) {
        this.#adapter.persist({ filingTypes: types });
    }

    onSelectFilingTags(tags: string[]) {
        this.#updateState({ type: 'UPDATE_SELECTED_FILING_TAGS', tags });
    }

    onSearchStringChange(searchString: string) {
        this.#updateState({ type: 'UPDATE_SEARCH_STRING', searchString });
    }

    onGoToDate(date: LocalDate | null) {
        this.#adapter.persist({ gotoDate: date });
    }

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#table.connectionStatus;
    }

    get progress(): ReadonlySignal<number | undefined> {
        return this.#table.progress;
    }

    refetch(): void {
        this.#table.refetch();
    }

    #resetState() {
        if (this.#stateSignal.value.type === 'initializing') {
            return;
        }

        this.#adapter.persist({ symbolFilter: null });

        this.#updateState({ type: 'RESET' });
    }

    #updateState(action: FilingsWidgetModelAction) {
        const prevState = this.#stateSignal.value;
        if (TRACE) {
            this.#logger.trace('Dispatching action', { type: action.type });
        }
        const newState = filingsWidgetModelReducer(this.#stateSignal.value, action);

        if (prevState !== newState) {
            if (TRACE) {
                this.#logger.trace('State updated', { action, prevState, state: newState });
            }

            this.#stateSignal.value = newState;
            this.#devtools?.send(action, this.#stateSignal.peek());

            // Keep slow idea source in sync with adapter — but only when no idea (screener/watchlist) is active
            if (
                this.#slowIdeaSourceSignal.value?.type !== 'idea' &&
                this.#adapter.getState().universeId !== (this.#slowIdeaSourceSignal.value?.id ?? null)
            ) {
                const adapterUniverseId = this.#adapter.getState().universeId;
                this.#slowIdeaSourceSignal.value = adapterUniverseId
                    ? { type: 'universe', id: adapterUniverseId }
                    : null;
            }

            if (newState.type === 'initializing') {
                this.#viewDetailsSignal.value = { type: 'loading' };
            } else if (newState.type === 'waiting-for-symbols') {
                this.#viewDetailsSignal.value = {
                    type: 'waiting-for-symbols',
                };
            } else {
                this.#viewDetailsSignal.value = {
                    type: 'ready',
                    instanceId: newState.instanceId,
                    symbols: computed(() => this.#symbols.value! as Set<string>),
                    retiringSymbols: computed(() => {
                        const state = this.#stateSignal.value;
                        return state.type === 'ready' ? state.untrackedSymbols : new Map();
                    }),
                    symbolFilter: this.#symbolFilter,
                    fastIdeas: computed(() => this.#fastIdeas.value ?? []),
                };
            }
        }
    }

    #buildEventBus(): FilingsWidgetModel['eventBus'] {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Filings-specific signals
                .addSignal('widget::instrument', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return '';
                        return state.symbolFilter ?? '';
                    }),
                )
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::symbolRetention', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return Duration.ofSeconds(0);
                        return state.symbolRetention;
                    }),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addSignal('widget::slowIdea', () => this.#slowIdeaDTO)
                .addSignal('widget::selectedFastIdeas', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return [];
                        return state.fastIdeas.map((idea) => ({
                            type: 'idea' as const,
                            id: idea.idea.id!,
                        }));
                    }),
                )
                .addSignal('widget::selectedSlowIdeas', () =>
                    computed(() => {
                        const source = this.#slowIdeaSourceSignal.value;
                        if (!source) return [];
                        return [{ type: source.type, id: source.id }];
                    }),
                )
                // Filings-specific events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.onChangeSymbolFilter(payload.instrument);
                })
                .addEvent('widget::updateSymbolRetention', (payload) => {
                    this.onChangeSymbolRetention(Duration.parse(payload.duration));
                })
                .addEvent('widget::onIdeasSelect', (payload) => {
                    // This event is only emitted for fast ideas by the IdeasMenu component
                    // (slow ideas use widget::selectSlowIdea instead)
                    const fastIdeas = payload.ideas.filter((idea) => !('universeId' in idea)) as (
                        | IdeaDTO<ScannerPlanDTO>
                        | IdeaDTO<SearchPlanDTO>
                    )[];
                    // Always forward to handler, even empty array (to clear selection)
                    this.onSelectFastIdeas(fastIdeas);
                })
                .addEvent('widget::onIdeaDelete', () => {
                    // Filings widget doesn't support idea deletion
                })
                .addEvent('widget::selectSlowIdea', (payload) => {
                    // Type assertion needed because event uses generic IdeaDTO while method expects specific plan types
                    this.onSelectSlowIdeas(payload.ideas as Parameters<typeof this.onSelectSlowIdeas>[0]);
                })
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.refetch();
                })
                // Filings second-row control signals
                .addSignal('widget::filingsGoToDate', () => this.#gotoDateSignal)
                .addSignal('widget::filingsSelectedFilingTypes', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return [];
                        return state.selectedFilingTypes;
                    }),
                )
                .addSignal('widget::filingsAvailableFilingTypes', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return [];
                        return state.filingTypes;
                    }),
                )
                .addSignal('widget::filingsSelectedFilingTags', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return [];
                        return state.selectedFilingTags;
                    }),
                )
                .addSignal('widget::filingsAvailableFilingTags', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return [];
                        return state.filingTags;
                    }),
                )
                // Filings second-row control events
                .addEvent('widget::filingsGoToDateChange', (payload) => {
                    this.onGoToDate(payload.date);
                })
                .addEvent('widget::filingsFilingTypeFilterChange', (payload) => {
                    this.onSelectFilingTypes(payload.types);
                })
                .addEvent('widget::filingsFilingTagsFilterChange', (payload) => {
                    this.onSelectFilingTags(payload.tags);
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(
                EventBusBuilder.create()
                    .addSignal('widget::name', () => signal(t('widgets::filings::name')))
                    .build(),
            ) as FilingsWidgetModel['eventBus'];
    }
}
